/**
 * Node.js adapter for MCP server
 *
 * Creates a standalone HTTP server for local development and testing.
 *
 * @example
 * ```typescript
 * import { createNodeServer } from 'docusaurus-plugin-mcp-server/adapters/node';
 *
 * const server = createNodeServer({
 *   docsPath: './build/mcp/docs.json',
 *   indexPath: './build/mcp/search-index.json',
 *   name: 'my-docs',
 * });
 *
 * server.listen(3456, () => {
 *   console.log('MCP server running at http://localhost:3456');
 * });
 * ```
 */

import { createServer, type Server, type IncomingMessage, type ServerResponse } from 'node:http';
import { McpDocsServer } from '../mcp/server.js';
import type { McpServerConfig } from '../types/index.js';

/**
 * Options for the Node.js MCP server.
 *
 * Accepts the same config as {@link McpDocsServer} — either file paths
 * (`docsPath`/`indexPath`, the usual local-dev case) or pre-loaded
 * `docs`/`searchIndexData` — plus a CORS override.
 */
export type NodeServerOptions = McpServerConfig & {
  /**
   * CORS origin to allow. Defaults to '*' (all origins).
   * Set to a specific origin or false to disable CORS headers.
   */
  corsOrigin?: string | false;
};

/**
 * Create a Node.js request handler for the MCP server.
 *
 * This returns a handler function compatible with `http.createServer()`.
 * For a complete server, use `createNodeServer()` instead.
 */
export function createNodeHandler(options: NodeServerOptions) {
  const { corsOrigin = '*', ...config } = options;
  let server: McpDocsServer | null = null;

  function getServer(): McpDocsServer {
    if (!server) {
      server = new McpDocsServer(config);
    }
    return server;
  }

  function setCorsHeaders(res: ServerResponse): void {
    if (corsOrigin !== false) {
      res.setHeader('Access-Control-Allow-Origin', corsOrigin);
      res.setHeader('Access-Control-Allow-Methods', 'GET, POST, OPTIONS');
      res.setHeader('Access-Control-Allow-Headers', 'Content-Type, Authorization');
    }
  }

  return async function handler(req: IncomingMessage, res: ServerResponse): Promise<void> {
    setCorsHeaders(res);

    // Handle CORS preflight
    if (req.method === 'OPTIONS') {
      res.writeHead(204);
      res.end();
      return;
    }

    // Handle GET requests for health check
    if (req.method === 'GET') {
      try {
        const mcpServer = getServer();
        const status = await mcpServer.getStatus();
        res.writeHead(200, { 'Content-Type': 'application/json' });
        res.end(JSON.stringify(status, null, 2));
      } catch (error) {
        const message = error instanceof Error ? error.message : String(error);
        res.writeHead(500, { 'Content-Type': 'application/json' });
        res.end(JSON.stringify({ error: message }));
      }
      return;
    }

    // Only allow POST requests for MCP
    if (req.method !== 'POST') {
      res.writeHead(405, { 'Content-Type': 'application/json' });
      res.end(
        JSON.stringify({
          jsonrpc: '2.0',
          id: null,
          error: {
            code: -32600,
            message: 'Method not allowed. Use POST for MCP requests, GET for status.',
          },
        })
      );
      return;
    }

    // Parse request body
    try {
      const body = await parseRequestBody(req);
      const mcpServer = getServer();
      await mcpServer.handleHttpRequest(req, res, body);
    } catch (error) {
      console.error('[MCP] Request error:', error);

      if (error instanceof Error && error.message === 'Request body too large') {
        res.writeHead(413, { 'Content-Type': 'application/json' });
        res.end(
          JSON.stringify({
            jsonrpc: '2.0',
            id: null,
            error: {
              code: -32600,
              message: 'Request body too large',
            },
          })
        );
        return;
      }

      if (error instanceof Error && error.message === 'Invalid JSON in request body') {
        res.writeHead(400, { 'Content-Type': 'application/json' });
        res.end(
          JSON.stringify({
            jsonrpc: '2.0',
            id: null,
            error: {
              code: -32700,
              message: 'Parse error: invalid JSON in request body',
            },
          })
        );
        return;
      }

      res.writeHead(500, { 'Content-Type': 'application/json' });
      res.end(
        JSON.stringify({
          jsonrpc: '2.0',
          id: null,
          error: {
            code: -32603,
            message: 'Internal server error',
          },
        })
      );
    }
  };
}

/**
 * Create a complete Node.js HTTP server for the MCP server.
 *
 * This is the simplest way to run an MCP server locally for development.
 */
export function createNodeServer(options: NodeServerOptions): Server {
  const handler = createNodeHandler(options);
  return createServer(handler);
}

const MAX_BODY_SIZE = 1024 * 1024; // 1MB

/**
 * Parse the request body as JSON
 */
async function parseRequestBody(req: IncomingMessage): Promise<unknown> {
  return new Promise((resolve, reject) => {
    let body = '';
    let size = 0;

    req.on('data', (chunk: Buffer | string) => {
      size += typeof chunk === 'string' ? Buffer.byteLength(chunk) : chunk.length;
      if (size > MAX_BODY_SIZE) {
        // Stop accumulating (bounded memory) and reject so the handler can send
        // a 413. Don't destroy the socket here, or the client never sees it.
        reject(new Error('Request body too large'));
        return;
      }
      body += chunk.toString();
    });
    req.on('end', () => {
      try {
        resolve(body ? JSON.parse(body) : undefined);
      } catch {
        reject(new Error('Invalid JSON in request body'));
      }
    });
    req.on('error', reject);
  });
}
